import { TickFormatter } from '@visx/axis'
import moment from 'moment'
import { Required } from 'types'
import { GetAnalyticsQueryParams } from 'apps/tms/types/apiTypes'
import { FORMATS } from 'constants/datetime'

type FilterBucket = 'daily' | 'weekly' | 'monthly'
type SelectedDateFilter = Required<GetAnalyticsQueryParams['dateFilter']>

export const SHIPMENTS_TOOLTIP =
  'This chart displays shipments in Completed or Delivered status with a pickup date within your selected time range.'

export const CARBON_EMISSIONS_TOOLTIP =
  'This chart displays your total estimated carbon emissions of your Full Truckload shipments within your selected time range. Metric tons are the standard measurement unit for corporate emissions of greenhouse gasses.'

export const SPEND_TOOLTIP =
  'This chart displays your total spend for shipments in Completed or Delivered status with a pickup date within your selected time range. Total spend includes accessorials.'

export const COST_PER_POUND_TOOLTIP =
  'Cost Per Pound is calculated by dividing a shipment’s Gross Weight by its total spend including accessorials. This chart displays your average Cost Per Pound for shipments in Completed or Delivered status with a pickup date within your selected time range. '

export const COST_PER_MILE_TOOLTIP =
  'Cost Per Mile is calculated by dividing a shipment’s total spend, including accessorials, by its miles amount. This chart displays your average Cost Per Mile for shipments in Completed or Delivered status with a pickup date within your selected time range.'

export const TENDER_ACCEPTANCE_TOOLTIP =
  'This chart shows the percentage of shipments accepted by Routing Guide, for only shipments in Delivered or Completed status.'

export const ASSIGNMENTS_TOOLTIP =
  '“Assignments” refers to the number of times this carrier was assigned to a shipment. Removals refer the number of times that carrier was removed from a shipment (excluding instances where a carrier was removed due to the shipment being canceled or deleted).'

export const filterBucketMapping: {
  [key in SelectedDateFilter]: FilterBucket
} = {
  '7_days': 'daily',
  '30_days': 'daily',
  '3_months': 'weekly',
  '6_months': 'weekly',
  '12_months': 'monthly',
  month_to_date: 'daily',
  year_to_date: 'monthly',
  custom_date: 'daily',
}

const bucketedDateFormats = {
  daily: FORMATS.monthDay,
  weekly: FORMATS.monthDay,
  monthly: FORMATS.monthYear,
}

export const bucketedTooltipDateFormats: { [key in FilterBucket]: string } = {
  daily: FORMATS.monthDay,
  weekly: `[Week of ]${FORMATS.monthDay}`,
  monthly: FORMATS.monthYear,
}

/**
 * Shared formatter function for Analytics charts using the date filter
 * configured here.
 */
export const xAxisTickFormatter = (bucket: FilterBucket): TickFormatter<Date> => {
  const bucketedFormat = bucketedDateFormats[bucket]
  return (date: Date) => moment.parseZone(date).format(bucketedFormat)
}
